import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import SiteLayout from '../../layouts/SiteLayout';
import Header from '../../components/Header/Header';
import Box from '../../components/Common/Box';
import { usePepites } from './usePepites';
import { useScreenType } from '../../hooks/useScreenType';

// Sub-components
import DesktopPepites from './DesktopPepites';
import MobilePepites from './MobilePepites';

const PepitesScreen: React.FC = () => {
    const { data, loading, error, refetch } = usePepites();
    const navigate = useNavigate();
    const { isMobile } = useScreenType();
    const [activeTab, setActiveTab] = useState<'opportunities' | 'sell' | 'universe' | 'info'>('opportunities');
    const [filters, setFilters] = useState({
        search: '',
        cciBreakout: false,
        zScoreExtreme: false,
        obvBullish: false
    });

    if (loading) {
        return (
            <SiteLayout>
                <Header title="Chasseur de Pépites" />
                <Box>
                    <div style={{ padding: 40, textAlign: 'center' }}>
                        <div className="loading-spinner" />
                        <p>Recherche de Momentum (CCI & OBV)...</p>
                    </div>
                </Box>
            </SiteLayout>
        );
    }

    if (error) {
        return (
            <SiteLayout>
                <Header title="Chasseur de Pépites" />
                <Box>
                    <div style={{ padding: 40, textAlign: 'center', color: '#dc2626' }}>
                        <p>Erreur: {error}</p>
                        <button 
                            onClick={refetch} 
                            style={{ 
                                marginTop: 16, 
                                padding: '10px 20px', 
                                backgroundColor: '#2563eb', 
                                color: 'white', 
                                border: 'none', 
                                borderRadius: 6, 
                                cursor: 'pointer' 
                            }}
                        >
                            Réessayer
                        </button>
                    </div>
                </Box>
            </SiteLayout>
        );
    }

    const opportunities = data?.opportunities || [];
    const sellSignals = data?.sell_signals || [];
    const eligibleUniverse = data?.eligible_universe || [];
    const sentiment = data?.market_sentiment || 'NEUTRAL';

    const sentimentColors: Record<string, { bg: string; color: string }> = {
        BULLISH: { bg: '#dcfce7', color: '#16a34a' },
        BEARISH: { bg: '#fee2e2', color: '#dc2626' },
        NEUTRAL: { bg: '#f1f5f9', color: '#475569' },
    };

    const filteredUniverse = eligibleUniverse.filter((stock: any) => {
        if (filters.search) {
            const searchLower = filters.search.toLowerCase();
            const matchesSearch =
                stock.symbole.toLowerCase().includes(searchLower) ||
                (stock.nom_societe && stock.nom_societe.toLowerCase().includes(searchLower));
            if (!matchesSearch) return false;
        }
        if (filters.cciBreakout && stock.cci <= 100) return false;
        if (filters.zScoreExtreme && (stock.z_score || 0) <= 2.0) return false;
        if (filters.obvBullish && stock.obv_trend !== 'BULLISH') return false;
        return true;
    });

    const childProps = {
        data,
        activeTab,
        setActiveTab,
        filters,
        setFilters,
        opportunities,
        sellSignals,
        filteredUniverse,
        sentiment,
        sentimentColors,
        navigate: (path: string) => navigate(path)
    };

    return (
        <SiteLayout>
            <Header title="Chasseur de Pépites" />
            {isMobile ? (
                <MobilePepites {...childProps} />
            ) : (
                <DesktopPepites {...childProps} />
            )}
        </SiteLayout>
    );
};

export default PepitesScreen;
